import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Crop, Pencil, RotateCw, Type, Undo2 } from "lucide-react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";

const CORES = [
  "#ffffff",
  "#000000",
  "#ef4444",
  "#f59e0b",
  "#22c55e",
  "#3b82f6",
  "#a855f7",
];

const MODOS = [
  { id: "recortar", rotulo: "Recortar", Icone: Crop },
  { id: "desenhar", rotulo: "Desenhar", Icone: Pencil },
  { id: "texto", rotulo: "Texto", Icone: Type },
];

// Tipos que o canvas sabe escrever de volta. Qualquer outro (gif animado,
// bmp) sai como png: perder a animação é melhor do que gravar um arquivo com
// extensão que não bate com o conteúdo — armadilha 13.
const SAIDAS = ["image/jpeg", "image/png", "image/webp"];

const novoCanvas = (largura, altura) => {
  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  return canvas;
};

// Gira 90° no sentido horário. O ponto (x, y) da imagem antiga vira
// (alturaAntiga - y, x) na nova — é por isso que traços e textos acompanham
// o giro sem se perder.
const girar = (base) => {
  const destino = novoCanvas(base.height, base.width);
  const ctx = destino.getContext("2d");
  ctx.translate(destino.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(base, 0, 0);
  return destino;
};

const girarPonto = (ponto, base) => ({
  ...ponto,
  x: base.height - ponto.y,
  y: ponto.x,
});

const fonteDoTexto = (tamanho) => `${Math.max(16, tamanho * 4)}px sans-serif`;

const reguaDeTexto = novoCanvas(1, 1).getContext("2d");

// Caixa do texto em volta do ponto onde ele foi solto — o desenho é centrado
// (`textAlign`/`textBaseline` no meio), então a caixa também é.
const caixaDoTexto = (item) => {
  reguaDeTexto.font = fonteDoTexto(item.tamanho);
  const altura = Math.max(16, item.tamanho * 4);
  return {
    largura: reguaDeTexto.measureText(item.texto).width,
    altura,
  };
};

// Desfaz a rotação do texto antes de comparar: sem isso, texto girado só
// responderia ao arrasto na área da caixa não girada, ao lado dele.
const dentroDoTexto = (ponto, item) => {
  const { largura, altura } = caixaDoTexto(item);
  const angulo = ((item.angulo || 0) * Math.PI) / 180;
  const dx = ponto.x - item.x;
  const dy = ponto.y - item.y;
  const lx = dx * Math.cos(angulo) + dy * Math.sin(angulo);
  const ly = -dx * Math.sin(angulo) + dy * Math.cos(angulo);
  return Math.abs(lx) <= largura / 2 + 8 && Math.abs(ly) <= altura / 2 + 8;
};

const pintarTraco = (ctx, traco) => {
  if (traco.pontos.length < 2) {
    // Um toque só ainda é um traço: vira ponto, senão clicar não marca nada.
    const [p] = traco.pontos;
    ctx.fillStyle = traco.cor;
    ctx.beginPath();
    ctx.arc(p.x, p.y, traco.tamanho / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  ctx.strokeStyle = traco.cor;
  ctx.lineWidth = traco.tamanho;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  traco.pontos.forEach((p, i) =>
    i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)
  );
  ctx.stroke();
};

const pintarTexto = (ctx, item) => {
  ctx.save();
  // Gira junto com a imagem: o texto foi escrito na orientação de então, e
  // desenhar sempre na horizontal deixaria a legenda deitada em relação ao
  // que ela aponta.
  ctx.translate(item.x, item.y);
  ctx.rotate(((item.angulo || 0) * Math.PI) / 180);
  ctx.font = fonteDoTexto(item.tamanho);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Contorno escuro: texto claro sobre foto clara some sem ele.
  ctx.lineWidth = Math.max(2, item.tamanho / 3);
  ctx.strokeStyle = item.cor === "#000000" ? "#ffffff" : "#000000";
  ctx.strokeText(item.texto, 0, 0);
  ctx.fillStyle = item.cor;
  ctx.fillText(item.texto, 0, 0);
  ctx.restore();
};

const montar = (base, tracos, textos) => {
  const canvas = novoCanvas(base.width, base.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(base, 0, 0);
  tracos.forEach((traco) => pintarTraco(ctx, traco));
  textos.forEach((texto) => pintarTexto(ctx, texto));
  return canvas;
};

const recortarCanvas = (canvas, area) => {
  const destino = novoCanvas(Math.round(area.w), Math.round(area.h));
  destino
    .getContext("2d")
    .drawImage(
      canvas,
      area.x,
      area.y,
      area.w,
      area.h,
      0,
      0,
      destino.width,
      destino.height
    );
  return destino;
};

const normalizar = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  w: Math.abs(a.x - b.x),
  h: Math.abs(a.y - b.y),
});

const EditorDeImagem = ({ arquivo, aberto, onFechar, onConcluir }) => {
  const canvasRef = useRef(null);
  const tracoVivo = useRef(null);
  const recorteVivo = useRef(null);
  const inicioDoArrasto = useRef(null);
  const textoArrastado = useRef(null);

  const [base, setBase] = useState(null);
  const [tracos, setTracos] = useState([]);
  const [textos, setTextos] = useState([]);
  const [recorte, setRecorte] = useState(null);
  const [historico, setHistorico] = useState([]);

  const [modo, setModo] = useState("recortar");
  const [cor, setCor] = useState("#ef4444");
  const [tamanho, setTamanho] = useState(8);
  const [textoAtual, setTextoAtual] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto || !arquivo) return undefined;

    const url = window.URL.createObjectURL(arquivo);
    const imagem = new Image();
    let vivo = true;

    imagem.onload = () => {
      if (!vivo) return;
      const canvas = novoCanvas(imagem.naturalWidth, imagem.naturalHeight);
      canvas.getContext("2d").drawImage(imagem, 0, 0);
      setBase(canvas);
      setTracos([]);
      setTextos([]);
      setRecorte(null);
      setHistorico([]);
      setModo("recortar");
    };
    imagem.src = url;

    return () => {
      vivo = false;
      window.URL.revokeObjectURL(url);
    };
  }, [aberto, arquivo]);

  const redesenhar = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !base) return;

    if (canvas.width !== base.width || canvas.height !== base.height) {
      canvas.width = base.width;
      canvas.height = base.height;
    }

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(base, 0, 0);

    [...tracos, tracoVivo.current]
      .filter(Boolean)
      .forEach((traco) => pintarTraco(ctx, traco));
    textos.forEach((texto) => pintarTexto(ctx, texto));

    const area = recorteVivo.current || recorte;
    if (area && area.w > 1 && area.h > 1) {
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(area.x + area.w, area.y, -area.w, area.h);
      ctx.fill("evenodd");
      ctx.restore();

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(2, canvas.width / 400);
      ctx.strokeRect(area.x, area.y, area.w, area.h);
    }
  }, [base, tracos, textos, recorte]);

  useEffect(() => {
    redesenhar();
  }, [redesenhar]);

  const guardar = () =>
    setHistorico((anterior) => [
      ...anterior.slice(-19),
      { base, tracos, textos, recorte },
    ]);

  const desfazer = () => {
    const ultimo = historico[historico.length - 1];
    if (!ultimo) return;
    setBase(ultimo.base);
    setTracos(ultimo.tracos);
    setTextos(ultimo.textos);
    setRecorte(ultimo.recorte);
    setHistorico((anterior) => anterior.slice(0, -1));
  };

  const aoGirar = () => {
    if (!base) return;
    guardar();
    setTracos((anterior) =>
      anterior.map((traco) => ({
        ...traco,
        pontos: traco.pontos.map((p) => girarPonto(p, base)),
      }))
    );
    setTextos((anterior) =>
      anterior.map((t) => ({
        ...girarPonto(t, base),
        angulo: ((t.angulo || 0) + 90) % 360,
      }))
    );
    setRecorte(null);
    recorteVivo.current = null;
    setBase(girar(base));
  };

  const aplicarRecorte = () => {
    if (!base || !recorte) return;
    guardar();
    setTracos((anterior) =>
      anterior.map((traco) => ({
        ...traco,
        pontos: traco.pontos.map((p) => ({
          ...p,
          x: p.x - recorte.x,
          y: p.y - recorte.y,
        })),
      }))
    );
    setTextos((anterior) =>
      anterior.map((t) => ({ ...t, x: t.x - recorte.x, y: t.y - recorte.y }))
    );
    setBase(recortarCanvas(base, recorte));
    setRecorte(null);
    recorteVivo.current = null;
  };

  // O canvas tem o tamanho real da imagem e o CSS o encolhe: sem converter
  // pelo `getBoundingClientRect` o traço sai longe do dedo em imagem grande.
  const pontoDoEvento = (e) => {
    const canvas = canvasRef.current;
    const area = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - area.left) / area.width) * canvas.width,
      y: ((e.clientY - area.top) / area.height) * canvas.height,
    };
  };

  const aoPressionar = (e) => {
    if (!base) return;
    const ponto = pontoDoEvento(e);

    if (modo === "texto") {
      // Clique em cima de um texto já posto move ele; no vazio, escreve um
      // novo. O último ganha, que é o que está por cima no desenho.
      const indice = textos.reduce(
        (achado, item, i) => (dentroDoTexto(ponto, item) ? i : achado),
        -1
      );
      if (indice >= 0) {
        guardar();
        e.currentTarget.setPointerCapture(e.pointerId);
        textoArrastado.current = {
          indice,
          dx: textos[indice].x - ponto.x,
          dy: textos[indice].y - ponto.y,
        };
        return;
      }

      const texto = textoAtual.trim();
      if (!texto) return;
      guardar();
      setTextos((anterior) => [
        ...anterior,
        { ...ponto, texto, cor, tamanho, angulo: 0 },
      ]);
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);

    if (modo === "desenhar") {
      tracoVivo.current = { cor, tamanho, pontos: [ponto] };
    } else {
      inicioDoArrasto.current = ponto;
      recorteVivo.current = { ...ponto, w: 0, h: 0 };
    }
    redesenhar();
  };

  const aoArrastar = (e) => {
    if (textoArrastado.current) {
      // Posição, não identidade: cada passo do arrasto cria um objeto novo,
      // e comparar por referência travaria o texto no primeiro pixel.
      const { indice, dx, dy } = textoArrastado.current;
      const ponto = pontoDoEvento(e);
      setTextos((anterior) =>
        anterior.map((t, i) =>
          i === indice ? { ...t, x: ponto.x + dx, y: ponto.y + dy } : t
        )
      );
      return;
    }
    if (tracoVivo.current) {
      tracoVivo.current.pontos.push(pontoDoEvento(e));
      redesenhar();
      return;
    }
    if (inicioDoArrasto.current) {
      recorteVivo.current = normalizar(inicioDoArrasto.current, pontoDoEvento(e));
      redesenhar();
    }
  };

  // Apagar um texto: duplo clique em cima dele. Entra no Desfazer como o
  // resto, então errar o alvo não custa nada.
  const aoDuplicarClique = (e) => {
    if (modo !== "texto" || !base) return;
    const ponto = pontoDoEvento(e);
    const indice = textos.reduce(
      (achado, item, i) => (dentroDoTexto(ponto, item) ? i : achado),
      -1
    );
    if (indice < 0) return;
    guardar();
    textoArrastado.current = null;
    setTextos((anterior) => anterior.filter((_, i) => i !== indice));
  };

  const aoSoltar = () => {
    if (textoArrastado.current) {
      textoArrastado.current = null;
      return;
    }
    if (tracoVivo.current) {
      const traco = tracoVivo.current;
      tracoVivo.current = null;
      guardar();
      setTracos((anterior) => [...anterior, traco]);
      return;
    }

    if (inicioDoArrasto.current) {
      const area = recorteVivo.current;
      inicioDoArrasto.current = null;
      recorteVivo.current = null;
      // Clique sem arrasto desfaz a seleção em vez de virar um recorte de
      // 1 px que o "Aplicar" aceitaria.
      setRecorte(area && area.w > 8 && area.h > 8 ? area : null);
    }
  };

  const salvar = async () => {
    if (!base) return;
    setSalvando(true);

    let canvas = montar(base, tracos, textos);
    if (recorte) canvas = recortarCanvas(canvas, recorte);

    const tipo = SAIDAS.includes(arquivo.type) ? arquivo.type : "image/png";

    canvas.toBlob(
      (blob) => {
        setSalvando(false);
        if (!blob) return;
        const nome =
          tipo === arquivo.type
            ? arquivo.name
            : `${arquivo.name.replace(/\.[^.]+$/, "")}.png`;
        onConcluir(new File([blob], nome, { type: tipo }));
      },
      tipo,
      0.92
    );
  };

  const podeDesfazer = historico.length > 0;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Editar imagem</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1">
          {MODOS.map(({ id, rotulo, Icone }) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={modo === id ? "default" : "ghost"}
              onClick={() => setModo(id)}
            >
              <Icone className="h-4 w-4" />
              {rotulo}
            </Button>
          ))}

          <Button type="button" size="sm" variant="ghost" onClick={aoGirar}>
            <RotateCw className="h-4 w-4" />
            Girar
          </Button>

          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={desfazer}
            disabled={!podeDesfazer}
          >
            <Undo2 className="h-4 w-4" />
            Desfazer
          </Button>

          {modo === "recortar" && recorte && (
            <Button type="button" size="sm" variant="secondary" onClick={aplicarRecorte}>
              <Check className="h-4 w-4" />
              Aplicar recorte
            </Button>
          )}
        </div>

        {modo !== "recortar" && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              {CORES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Cor ${c}`}
                  onClick={() => setCor(c)}
                  style={{ backgroundColor: c }}
                  className={cn(
                    "h-6 w-6 rounded-full border transition",
                    cor === c && "ring-2 ring-ring ring-offset-2 ring-offset-background"
                  )}
                />
              ))}
            </div>

            <label className="flex flex-1 items-center gap-2 text-xs text-muted-foreground">
              Tamanho
              <input
                type="range"
                min="2"
                max="40"
                value={tamanho}
                onChange={(e) => setTamanho(Number(e.target.value))}
                className="w-32 accent-primary"
              />
            </label>

            {modo === "texto" && (
              <input
                type="text"
                value={textoAtual}
                onChange={(e) => setTextoAtual(e.target.value)}
                placeholder="Escreva e toque na imagem"
                className="min-w-40 flex-1 rounded-lg border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              />
            )}
          </div>
        )}

        <div className="flex max-h-[55dvh] justify-center overflow-hidden rounded-lg bg-muted/40 p-2">
          <canvas
            ref={canvasRef}
            onPointerDown={aoPressionar}
            onPointerMove={aoArrastar}
            onPointerUp={aoSoltar}
            onDoubleClick={aoDuplicarClique}
            onPointerCancel={aoSoltar}
            className="max-h-[52dvh] max-w-full touch-none object-contain"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {modo === "recortar"
            ? "Arraste sobre a imagem para escolher a área e clique em Aplicar recorte."
            : modo === "desenhar"
            ? "Arraste sobre a imagem para desenhar."
            : "Escreva o texto acima e toque na imagem para colocá-lo. Arraste um texto já posto para movê-lo, ou dê dois cliques nele para apagar."}
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button type="button" onClick={salvar} disabled={!base || salvando}>
            <Check className="h-4 w-4" />
            Concluir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditorDeImagem;
